import { AnyFSA } from '@makemydeal/dr-platform-shared';
import { Middleware } from 'redux';
import { getApplicationName } from './applicationContext';
import { safeClone } from './safeClone';
import { EventMiddlewareConfig, EventMiddlewareConfigV3 } from './types';

let eventMapKeys: string[];

/**
 * Ensures the consuming application's own `application.name` is present at the top level of an
 * outgoing CDL envelope, so persisted state is corrected on every event (DE751628).
 *
 * Leaves the envelope untouched when there is nothing to do — no recorded name, or a push with no
 * `event` grouping (readiness and state-only pushes look like `{ dataLayerReady: true, ... }`).
 * Those pass-through cases are handed on by reference, exactly as the default push path does; only
 * an envelope this function actually rewrites is snapshotted.
 */
const withApplicationName = (envelope: any): any => {
    const name = getApplicationName();
    if (!name || !envelope || typeof envelope !== 'object') {
        return envelope;
    }
    if (typeof envelope.event !== 'object' || envelope.event === null) {
        return envelope;
    }

    // Snapshot before rewriting. A producer typically builds its payload from live state — the
    // relay contract calls `event.componentContext` "originating PBC state minus event" — and a
    // shallow copy would leave the published envelope aliasing that state, so a later mutation
    // anywhere in the chain would rewrite an event already recorded. See `safeClone`.
    const snapshot = safeClone(envelope);

    // safeClone passes non-plain objects through by reference, so force a fresh plain object here
    // before mutating it — otherwise `delete` below could reach back into the producer's own event
    // node if it were ever something other than a plain object literal.
    snapshot.event = { ...snapshot.event };

    // Drop any event-level `application` in full — see `alwaysIncludeAppName` in types.ts for why.
    delete snapshot.event.application;

    // snapshot.application is already a fresh object from safeClone, so this mutates it in place
    // rather than spreading a second copy. Respect an explicit top-level name; only fill in the
    // recorded one when absent.
    snapshot.application = snapshot.application ?? {};
    snapshot.application.name = snapshot.application.name ?? name;

    return snapshot;
};

/**
 * Produces a middleware that handles pushing events to a data layer
 * @typeParam EventMapper - Mapping of redux action type strings to an analytics event producer
 * @param dataLayer - The dataLayer array of analytics events
 * @returns Middleware function
 */
export const buildGTMEventMiddleware = ({
    eventMap,
    dataLayerName,
    enabled = true,
    middlewareEnabledSelector
}: EventMiddlewareConfig): Middleware => ({ getState }) => (next) => (action: AnyFSA) => {
    next(action);
    let isCdlV3Enabled = false;
    try {
        isCdlV3Enabled = middlewareEnabledSelector(getState());
    } catch (e) {
        console.warn('Unable to determine CDL enabled state because app initialization sequence is invalid');
    }
    if (!enabled || isCdlV3Enabled) return;
    if (!eventMapKeys) {
        eventMapKeys = Object.keys(eventMap);
    }
    const shouldProccessAction = eventMapKeys.includes(action.type) && window[dataLayerName];
    if (shouldProccessAction) {
        const mappedEvent = eventMap[action.type];
        const event = typeof mappedEvent === 'function' ? mappedEvent(action, getState()) : mappedEvent;
        window[dataLayerName].push(event);
    }
};

/**
 * Produces a middleware that handles pushing events to a data layer
 * @typeParam EventMapper - Mapping of redux action type strings to an analytics event producer
 * @param coxAutoDataLayer - The coxAutoDataLayer array of analytics events
 * @returns Middleware function
 */
export const buildCDLEventMiddlewareV3 = ({
    eventMap,
    enabled = true,
    middlewareEnabledSelector,
    alwaysIncludeAppName = false
}: EventMiddlewareConfigV3): Middleware => {
    let eventMapKeysV3: string[];
    return ({ getState }) => (next) => (action: AnyFSA) => {
        next(action);
        let isCdlV3Enabled = false;
        try {
            isCdlV3Enabled = middlewareEnabledSelector(getState());
        } catch (e) {
            console.warn('Unable to determine CDL enabled state because app initialization sequence is invalid');
        }

        if (!enabled || !isCdlV3Enabled) return;
        if (!eventMapKeysV3) {
            eventMapKeysV3 = Object.keys(eventMap);
        }
        const shouldProccessAction = eventMapKeysV3.includes(action.type) && window.coxAutoDataLayer;
        if (shouldProccessAction) {
            const mappedEvent = eventMap[action.type];
            const event = typeof mappedEvent === 'function' ? mappedEvent(action, getState()) : mappedEvent;
            if (!event) return;
            window.coxAutoDataLayer.push(alwaysIncludeAppName ? withApplicationName(event) : event);
        }
    };
};
